import { Directive, ElementRef, Input, OnChanges, Optional, Renderer2, RendererStyleFlags2, Self, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatMenuItem } from '@angular/material/menu';
import { ActionContext, ActionDefinition, AppView } from 'app/shared/action.types';
import { ActionRegistryService } from 'app/shared/action-registry.service';

// Generic permission-gated action directive.
@Directive({
    selector: '[appAction]',
    exportAs: 'appAction',
    standalone: true,
})
export class ActionDirective implements OnChanges {
    @Input('appAction') context: ActionContext;

    private hasView = false;
    private definition: ActionDefinition | undefined;
    private allowed = false;

    constructor(
        private registry: ActionRegistryService,
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        @Optional() private templateRef: TemplateRef<any>,
        @Optional() private viewContainer: ViewContainerRef,
        @Optional() @Self() private matMenuItem: MatMenuItem,
        @Optional() @Self() private matButton: MatButton,
    ) {
        // Lets the title tooltip still show on hover once disabled - see styles.scss.
        if (!this.templateRef) {
            this.renderer.addClass(this.elementRef.nativeElement, 'app-action-host');
        }
    }

    // Explanation for why the action is blocked, e.g. [matTooltip]="ref.deniedReason ?? 'Download'".
    get deniedReason(): string | null {
        if (this.allowed || !this.definition) {
            return null;
        }
        if (this.context?.deniedReason) {
            return this.context.deniedReason;
        }
        const reason = this.definition.deniedReason;
        if (!reason) {
            return null;
        }
        if (typeof reason !== 'function') {
            return reason;
        }
        const item = this.context?.item;
        if (item == null) {
            return null;
        }
        return reason(Array.isArray(item) ? item[0] : item);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['context']) {
            this.updateView();
        }
    }

    private updateView(): void {
        this.definition = this.context?.action ? this.registry.resolve(this.context.action) : undefined;

        const hidden = this.evaluate(this.definition?.hide);
        // Hide always wins - no point disabling something that's already gone.
        const disabled = !hidden && this.evaluate(this.definition?.disable);
        this.allowed = !hidden && !disabled;
        if (this.templateRef) {
            // Structural usage can only add/remove the element, so a disabled action is hidden too.
            this.applyHide(hidden || disabled);
        } else {
            this.applyHide(hidden);
            this.applyDisable(disabled);
        }
        this.applyTitle();
    }

    // Native title tooltip still shows on hover over a disabled element, unlike MatTooltip.
    private applyTitle(): void {
        if (this.templateRef) {
            return;
        }
        if (this.deniedReason) {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'title', this.deniedReason);
        } else {
            this.renderer.removeAttribute(this.elementRef.nativeElement, 'title');
        }
    }

    // No item to check against, or an empty bulk selection, counts as blocked. An array is blocked if any entry is.
    private evaluate(predicate: ((item: any, view?: AppView) => boolean) | undefined): boolean {
        if (!predicate) {
            return false;
        }
        const item = this.context?.item;
        const view = this.context?.view;
        if (item == null) {
            return true;
        }
        if (Array.isArray(item)) {
            return item.length === 0 || item.some((entry) => predicate(entry, view));
        }
        return predicate(item, view);
    }

    // Removes/restores the embedded view.
    private applyHide(hide: boolean): void {
        if (this.templateRef && this.viewContainer) {
            if (hide && this.hasView) {
                this.viewContainer.clear();
                this.hasView = false;
            } else if (!hide && !this.hasView) {
                this.viewContainer.createEmbeddedView(this.templateRef);
                this.hasView = true;
            }
            return;
        }
        this.renderer.setProperty(this.elementRef.nativeElement, 'hidden', hide);
        if (hide) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'none', RendererStyleFlags2.Important);
        } else {
            this.renderer.removeStyle(this.elementRef.nativeElement, 'display');
        }
    }

    // Sets disabled on the host component.
    private applyDisable(disabled: boolean): void {
        if (this.matMenuItem) {
            this.matMenuItem.disabled = disabled;
        } else if (this.matButton) {
            this.matButton.disabled = disabled;
        } else {
            this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', disabled);
        }
    }
}
